import { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react';
import { Layout } from '@/components/common/Layout/Layout';
import { PageHeader, PageShell } from '@/components/ui/page-shell';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/lib/store';
import { ROUTES } from '@/constants/routes';
import api, { showErrorToast } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Textarea } from '@/components/ui/textarea';
import { ComboboxSelect } from '@/components/ui/combobox-select';
import { UserCombobox } from '@/components/common/EntityCombobox';
import { DatePicker } from '@/components/ui/date-picker';
import { Switch } from '@/components/ui/switch';
import {
  Download,
  FileEdit,
  RotateCcw,
  CalendarDays,
  X,
  CheckCircle,
} from 'lucide-react';
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from 'date-fns';
import { TablePagination } from '@/components/ui/table-pagination';
import { cn } from "@/lib/utils";
import { Link } from 'react-router-dom';
import { FilterableColumnHeader, ColumnHeaderContext, BulkEditDialog, ActionDropdown } from '@/components/timesheet';
import { decimalToHHMM, hhmmToDecimal } from '@/utils/time-format';
import { isSuperUser as checkSuperUser, isAdmin as checkAdmin, isManager as checkManager } from '@/lib/permissions';
import type { TimesheetEntry, FilterState } from '@/types/timesheet';
import { autoResizeTextarea } from '@/types/timesheet';



// Types for stable row callbacks to prevent unnecessary re-renders
interface RowCallbacks {
  onEntryChange: (index: number, field: keyof TimesheetEntry, value: any) => void;
  onHoursBlur: (index: number, value: string) => void;
  onBillableHoursBlur: (index: number, value: string) => void;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onStartTimer: (id: number) => void;
  onStopTimer: (id: number) => void;
  onLock: (id: number) => void;
  onUnlock: (id: number) => void;
  onApprove: (id: number) => void;
}

interface TimesheetRowProps {
  entry: TimesheetEntry;
  index: number;
  isSelected: boolean;
  isUserAdmin: boolean;
  canManageEntries: boolean;
  canEdit: boolean;
  users: any[];
  matters: any[];
  userMap: Map<number, string>;
  formatMatterName: (id: number) => string;
  onToggleSelect: (id: number) => void;
  callbacks: RowCallbacks;
}

// Memoized row component — only re-renders when its own entry/selection changes
const TimesheetRow = memo(function TimesheetRow({
  entry, index, isSelected, isUserAdmin, canManageEntries, canEdit,
  users, matters, userMap, formatMatterName, onToggleSelect, callbacks
}: TimesheetRowProps) {
  const {
    onEntryChange, onHoursBlur, onBillableHoursBlur: _onBillableHoursBlur,
    onDelete, onDuplicate, onStartTimer, onStopTimer,
    onLock, onUnlock, onApprove
  } = callbacks;

  // Cache all the heavy cells (Selects, Textareas, Inputs, ActionDropdown).
  // This only recomputes when entry data changes — NOT when isSelected flips,
  // so "select all" only pays the cost of re-rendering the checkbox per row.
  const contentCells = useMemo(() => (
    <>
      {isUserAdmin && (
        <TableCell className="w-28 py-2">
          <UserCombobox
            value={entry.user ?? null}
            onChange={(id) => id && onEntryChange(index, 'user', id)}
            users={users}
            disabled={!canEdit && !entry.isNew}
            triggerClassName="h-auto min-h-[40px] p-2 text-sm [&>span]:whitespace-normal [&>span]:break-words"
          />
        </TableCell>
      )}

      <TableCell className="w-28 align-middle py-2">
        <DatePicker
          value={entry.date || ''}
          onChange={(iso) => onEntryChange(index, 'date', iso)}
          className="h-8 w-full px-2"
          disabled={!canEdit}
          clearable={false}
        />
      </TableCell>

      <TableCell className="w-32 align-middle py-2">
        <ComboboxSelect
          value={entry.matter ? entry.matter.toString() : ''}
          onValueChange={(value) => onEntryChange(index, 'matter', Number(value))}
          placeholder="Select matter"
          searchPlaceholder="Search matters…"
          disabled={!canEdit}
          triggerClassName="h-auto min-h-[40px] text-sm text-left p-2 w-full whitespace-normal"
          popoverClassName="w-[320px]"
          options={matters.map((m) => ({
            value: m.id.toString(),
            label: m.group_name ? `${m.group_name} - ${m.name}` : m.name,
            searchableText: `${m.group_name ?? ''} ${m.name ?? ''}`,
          }))}
          renderValue={() => (
            entry.matter ? (
              <span className="whitespace-normal break-words leading-tight">
                {formatMatterName(entry.matter)}
              </span>
            ) : null
          )}
        />
        {/* Per-entry phase override. Shown only when the selected matter
            has more than one phase; otherwise there's nothing to pick. */}
        {(() => {
          const mat = matters.find((m) => m.id === entry.matter);
          const phases: Array<{ id: number; name: string }> = Array.isArray((mat as any)?.phases)
            ? (mat as any).phases
            : [];
          if (phases.length < 2) return null;
          return (
            <div className="mt-1">
              <ComboboxSelect
                value={entry.phase != null ? String(entry.phase) : ''}
                onValueChange={(value) =>
                  onEntryChange(index, 'phase', value ? Number(value) : null)
                }
                placeholder="Auto (by date)"
                searchPlaceholder="Search phases…"
                disabled={!canEdit}
                triggerClassName="h-7 text-xs p-1 w-full"
                popoverClassName="w-[240px]"
                options={[
                  { value: '', label: '— Auto (by date) —' },
                  ...phases.map((ph) => ({
                    value: String(ph.id),
                    label: ph.name,
                  })),
                ]}
              />
            </div>
          );
        })()}
      </TableCell>

      <TableCell className="w-48 py-2">
        <Textarea
          ref={(el) => {
            if (el && entry.description) {
              setTimeout(() => autoResizeTextarea(el), 0);
            }
          }}
          value={entry.description}
          onChange={(e) => onEntryChange(index, 'description', e.target.value)}
          placeholder="Description"
          className={cn(
            "min-h-[40px] text-sm resize-none overflow-hidden w-full leading-relaxed",
            !canEdit && "disabled:opacity-100 disabled:cursor-not-allowed"
          )}
          disabled={!canEdit}
          rows={1}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            autoResizeTextarea(target);
          }}
        />
      </TableCell>

      <TableCell className="w-20 align-middle py-2">
        <Input
          value={entry.hours}
          onChange={(e) => onEntryChange(index, 'hours', e.target.value)}
          onBlur={(e) => onHoursBlur(index, e.target.value)}
          placeholder="0"
          className="h-8 w-full text-center"
          disabled={!canEdit}
        />
      </TableCell>

      <TableCell className="w-16 align-middle py-2">
        <div className="flex justify-center">
          <Switch
            checked={entry.billable}
            onCheckedChange={(checked) => onEntryChange(index, 'billable', checked)}
            disabled={!canEdit}
          />
        </div>
      </TableCell>

      <TableCell className="w-16 align-middle py-2">
        <div className="flex items-center gap-1">
          {/* Status indicators */}
          {/* Actions dropdown */}
          <ActionDropdown
            entry={entry}
            onEdit={(field, value) => onEntryChange(index, field, value)}
            onDelete={() => onDelete(index)}
            onStartTimer={() => entry.id && onStartTimer(entry.id)}
            onStopTimer={() => entry.id && onStopTimer(entry.id)}
            onLock={() => entry.id && onLock(entry.id)}
            onUnlock={() => entry.id && onUnlock(entry.id)}
            onApprove={() => entry.id && onApprove(entry.id)}
            onDuplicate={() => onDuplicate(index)}
            canEdit={canEdit}
            canManage={canManageEntries}
            isAdmin={isUserAdmin}
          />
        </div>
      </TableCell>
    </>
  ), [entry, index, isUserAdmin, canManageEntries, canEdit, users, matters, userMap, formatMatterName, callbacks]);

  return (
    <TableRow
      className={cn(
        entry.timer_active && "bg-yellow-50",
        entry.reviewed && "bg-green-50",
        entry.locked && "bg-orange-50",
        entry.invoiced && "bg-blue-50"
      )}
    >
      <TableCell className="w-12 align-middle py-2">
        {entry.id && !entry.isNew && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(entry.id!)}
          />
        )}
      </TableCell>
      {contentCells}
    </TableRow>
  );
});

export function TimesheetList() {
  const { user } = useAuth();
  const userGroups = user?.groups || [];
  const { matters, users, fetchAll, isLoading: storeLoading } = useStore();
  
  // State variables
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  // Selection sum state
  interface SelectionSum {
    hours: number;
    // Visible to admin/accounting only (post-write-down). The backend
    // omits this field for other roles.
    billable_hours?: number;
    // Visible to every role — what the worker recorded.
    theoretical_billable_hours?: number;
    avg_rate?: number;
    total_fee?: number;
  }
  const [selectionSum, setSelectionSum] = useState<SelectionSum | null>(null);
  const [isSumLoading, setIsSumLoading] = useState(false);
  const sumAbortRef = useRef<AbortController | null>(null);
  const isAllSelectedRef = useRef(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const itemsPerPage = 20;
  
  // Filters
  const [filters, setFilters] = useState<FilterState>({
    userIds: user?.id ? [user.id] : [],
    matterIds: [],
    dateFrom: null,
    dateTo: null,
    billable: null,
    reviewed: null,
    invoiced: null,
    locked: null,
    sortBy: ['-date'], // Default sort by date descending
  });
  
  const saveTimers = useRef<{[key: string]: NodeJS.Timeout}>({});
  const filterDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const entriesRef = useRef<TimesheetEntry[]>([]);
  const savingIds = useRef<Set<string>>(new Set());

  // User permissions
  const isSuperUser = checkSuperUser(userGroups);
  const isAdmin = checkAdmin(userGroups);
  const isManager = checkManager(userGroups);

  const canManageEntries = isSuperUser || isAdmin || isManager;
  const isUserAdmin = isSuperUser || isAdmin;

  // Memoized lookup maps for O(1) access instead of O(n) .find() calls
  const matterMap = useMemo(() => {
    const map = new Map<number, string>();
    matters.forEach(m => {
      map.set(m.id, m.group_name ? `${m.group_name} - ${m.name}` : m.name);
    });
    return map;
  }, [matters]);

  const userMap = useMemo(() => {
    const map = new Map<number, string>();
    users.forEach(u => {
      map.set(u.id, `${u.first_name} ${u.last_name}`);
    });
    return map;
  }, [users]);

  const todayStr = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

  // Memoized filter options to prevent array recreation on every render
  const userFilterOptions = useMemo(() =>
    users.map(u => ({
      label: `${u.first_name} ${u.last_name}`,
      value: u.id
    })),
    [users]
  );

  const matterFilterOptions = useMemo(() =>
    matters.map(m => ({
      label: m.group_name ? `${m.group_name} - ${m.name}` : m.name,
      value: m.id
    })),
    [matters]
  );

  // Update filters for admin users - they can see all users by default
  useEffect(() => {
    if (isUserAdmin && filters.userIds.length === 1 && filters.userIds[0] === user?.id) {
      setFilters(prev => ({ ...prev, userIds: [] }));
    }
  }, [isUserAdmin, user?.id]);

  // Initialize data
  useEffect(() => {
    fetchAll();
  }, []);

  // Debounced fetch entries when filters change
  useEffect(() => {
    if (!storeLoading && matters.length > 0) {
      // Clear existing timer
      if (filterDebounceTimer.current) {
        clearTimeout(filterDebounceTimer.current);
      }
      
      // Set new timer - wait 500ms after user stops interacting
      filterDebounceTimer.current = setTimeout(() => {
        fetchEntries();
      }, 500);
    }
    
    return () => {
      if (filterDebounceTimer.current) {
        clearTimeout(filterDebounceTimer.current);
      }
    };
  }, [filters, currentPage, searchQuery, storeLoading, matters.length]);

  // Build sum query params — reuses the same filter logic as the list view
  const buildSumFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.userIds.length > 0) params.append('user_id', filters.userIds.join(','));
    if (filters.matterIds.length > 0) params.append('matter', filters.matterIds.join(','));
    if (filters.dateFrom) params.append('start_date', filters.dateFrom);
    if (filters.dateTo) params.append('end_date', filters.dateTo);
    if (filters.billable !== null) params.append('billable', filters.billable.toString());
    if (filters.reviewed !== null) params.append('reviewed', filters.reviewed.toString());
    if (filters.locked !== null) params.append('locked', filters.locked.toString());
    if (searchQuery) params.append('search', searchQuery);
    // Always exclude invoiced entries from the sum
    params.append('invoiced', 'false');
    return params;
  }, [filters, searchQuery]);

  // Fetch selection sum when selectedIds changes
  useEffect(() => {
    // Cancel any in-flight request
    if (sumAbortRef.current) {
      sumAbortRef.current.abort();
      sumAbortRef.current = null;
    }

    if (selectedIds.size === 0) {
      setSelectionSum(null);
      setIsSumLoading(false);
      return;
    }

    setIsSumLoading(true);
    const timer = setTimeout(async () => {
      const controller = new AbortController();
      sumAbortRef.current = controller;
      try {
        let params: URLSearchParams;
        if (isAllSelectedRef.current) {
          // "Select all" — use the same filters as the list view
          params = buildSumFilterParams();
        } else {
          // Partial selection — send individual IDs
          params = new URLSearchParams();
          selectedIds.forEach(id => params.append('ids', id.toString()));
          params.append('invoiced', 'false');
        }
        const response = await api.get(`${ROUTES.API.TIMESHEET_SUM}?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setSelectionSum(response.data);
        }
      } catch (error: any) {
        if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
          console.error('Sum fetch error:', error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSumLoading(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      if (sumAbortRef.current) {
        sumAbortRef.current.abort();
        sumAbortRef.current = null;
      }
    };
  }, [selectedIds, buildSumFilterParams]);

  // Auto-save timer
  useEffect(() => {
    return () => {
      Object.values(saveTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  // Keep entries ref in sync for async save operations
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  const fetchEntries = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams();
      
      queryParams.append('page', currentPage.toString());
      queryParams.append('page_size', itemsPerPage.toString());
      
      // Support multiple users and matters with CSV format
      if (filters.userIds.length > 0) {
        queryParams.append('user_id', filters.userIds.join(','));
      }
      if (filters.matterIds.length > 0) {
        queryParams.append('matter', filters.matterIds.join(','));
      }
      
      if (filters.dateFrom) queryParams.append('start_date', filters.dateFrom);
      if (filters.dateTo) queryParams.append('end_date', filters.dateTo);
      if (filters.billable !== null) queryParams.append('billable', filters.billable.toString());
      if (filters.reviewed !== null) queryParams.append('reviewed', filters.reviewed.toString());
      if (filters.invoiced !== null) queryParams.append('invoiced', filters.invoiced.toString());
      if (filters.locked !== null) queryParams.append('locked', filters.locked.toString());
      // Send multiple sort fields as comma-separated string
      if (filters.sortBy.length > 0) queryParams.append('sort_by', filters.sortBy.join(','));
      if (searchQuery) queryParams.append('search', searchQuery);
      
      const response = await api.get(`${ROUTES.API.TIMESHEET}?${queryParams.toString()}`);
      
            const processedEntries = response.data.results.map((entry: any) => ({
        id: entry.id,
        date: entry.date ? format(new Date(entry.date), 'yyyy-MM-dd') : todayStr,
        matter: entry.matter,
        user: entry.user,
        description: entry.description,
        hours: entry.hours ? decimalToHHMM(parseFloat(entry.hours)) : '',
        billable: entry.billable,
        timer_active: entry.timer_active,
        timer_start: entry.timer_start,
        matterName: formatMatterName(entry.matter),
        reviewed: entry.reviewed,
        locked: entry.locked,
        invoiced: entry.invoiced,
        // Worker view falls back to theoretical when admin's billable_hours
        // isn't visible (it's stripped from non-admin responses).
        billable_hours: entry.billable ?
          (entry.billable_hours
            ? decimalToHHMM(parseFloat(entry.billable_hours.toString()))
            : entry.theoretical_billable_hours
              ? decimalToHHMM(parseFloat(entry.theoretical_billable_hours.toString()))
              : (entry.hours ? decimalToHHMM(parseFloat(entry.hours)) : '00:00')) :
          '00:00',
        fee: entry.fee,
        fixed_fee: entry.fixed_fee,
        rate: entry.rate,
        net_rate: entry.net_rate,
        created_at: entry.created_at,
        updated_at: entry.updated_at,
      }));

      // Add new entry row at the end
      processedEntries.push({
        id: undefined,
        date: todayStr,
        matter: 0,
        description: '',
        hours: '',
        billable: true,
        timer_active: false,
        reviewed: false,
        isNew: true,
        matterName: '',
        locked: false,
        invoiced: false,
        billable_hours: '00:00',
        fee: undefined,
        fixed_fee: undefined,
        rate: undefined,
        net_rate: undefined,
        user: filters.userIds.length === 1 ? filters.userIds[0] : user?.id,
      });
      
      setEntries(processedEntries);
      setTotalEntries(response.data.count || 0);
    } catch (error) {
      console.error('Fetch error:', error);
      toast({ title: 'Error', description: 'Failed to fetch time entries', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatMatterName = useCallback((matterId: number) => {
    return matterMap.get(matterId) || "Unknown Matter";
  }, [matterMap]);

  const handleEntryChange = (index: number, field: keyof TimesheetEntry, value: any) => {
    // Use functional state update to avoid stale closure issues
    setEntries(prev => {
      const updatedEntries = [...prev];
      const entry = updatedEntries[index];
      
      if (field === 'hours') {
        updatedEntries[index] = { ...entry, [field]: value };
        if (entry.billable && !entry.fixed_fee) {
          let hoursDecimal: number;
          if (value.includes(':')) {
            hoursDecimal = hhmmToDecimal(value);
          } else {
            hoursDecimal = parseFloat(value) || 0;
          }
          updatedEntries[index].billable_hours = decimalToHHMM(hoursDecimal);
        } else {
          updatedEntries[index].billable_hours = '00:00';
        }
      } else if (field === 'matter') {
        updatedEntries[index] = { 
          ...entry, 
          [field]: Number(value), 
          matterName: formatMatterName(Number(value))
        };
      } else if (field === 'billable') {
        updatedEntries[index] = { ...entry, [field]: value };
        if (value && entry.hours) {
          let hoursDecimal: number;
          if (entry.hours.includes(':')) {
            hoursDecimal = hhmmToDecimal(entry.hours);
          } else {
            hoursDecimal = parseFloat(entry.hours) || 0;
          }
          updatedEntries[index].billable_hours = decimalToHHMM(hoursDecimal);
        } else {
          updatedEntries[index].billable_hours = '00:00';
        }
      } else {
        updatedEntries[index] = { ...entry, [field]: value };
      }
      
      // Add new row if needed
      if (index === updatedEntries.length - 1 && 
          (updatedEntries[index].matter || updatedEntries[index].description || updatedEntries[index].hours)) {
        updatedEntries.push({
          id: undefined,
          date: todayStr,
          matter: 0,
          description: '',
          hours: '',
          billable: true,
          timer_active: false,
          reviewed: false,
          isNew: true,
          matterName: '',
          locked: false,
          invoiced: false,
          billable_hours: '00:00',
          fee: undefined,
          fixed_fee: undefined,
          rate: undefined,
          net_rate: undefined,
          user: filters.userIds.length === 1 ? filters.userIds[0] : user?.id,
        });
      }
      
      return updatedEntries;
    });
    
    // Schedule auto-save (uses ref to check latest state when timer fires)
    const debounceKey = `save_${index}`;
    if (saveTimers.current[debounceKey]) {
      clearTimeout(saveTimers.current[debounceKey]);
    }
    
    saveTimers.current[debounceKey] = setTimeout(() => {
      const currentEntry = entriesRef.current[index];
      if (currentEntry?.matter && currentEntry?.description && currentEntry?.hours) {
        saveEntry(index);
      }
    }, 1500);
  };

  const formatHoursOnBlur = (index: number, value: string) => {
    if (!value || value === '0') {
      handleEntryChange(index, 'hours', '');
      return;
    }
    
    let hours: number;
    if (value.includes(':')) {
      hours = hhmmToDecimal(value);
    } else {
      hours = parseFloat(value);
    }
    
    if (!isNaN(hours)) {
      const formattedHours = decimalToHHMM(hours);
      handleEntryChange(index, 'hours', formattedHours);
      
      // Cancel the debounced save set by handleEntryChange and save immediately instead
      const debounceKey = `save_${index}`;
      if (saveTimers.current[debounceKey]) {
        clearTimeout(saveTimers.current[debounceKey]);
        delete saveTimers.current[debounceKey];
      }
      
      // Let the state update settle, then save using latest ref
      requestAnimationFrame(() => {
        const currentEntry = entriesRef.current[index];
        if (currentEntry?.matter && currentEntry?.description) {
          saveEntry(index);
        }
      });
    }
  };

  const formatBillableHoursOnBlur = (index: number, value: string) => {
    const entry = entriesRef.current[index];
    if (!entry) return;
    
    // If not billable, always set to zero
    if (!entry.billable) {
      handleEntryChange(index, 'billable_hours', '00:00');
      return;
    }
    
    if (!value || value === '0') {
      handleEntryChange(index, 'billable_hours', '');
      return;
    }
    
    let hours: number;
    if (value.includes(':')) {
      hours = hhmmToDecimal(value);
    } else {
      hours = parseFloat(value);
    }
    
    if (!isNaN(hours)) {
      // Convert to HH:MM format for display
      const formattedHours = decimalToHHMM(hours);
      handleEntryChange(index, 'billable_hours', formattedHours);
    }
  };

  const saveEntry = async (index: number) => {
    const entry = entriesRef.current[index];
    if (!entry || !entry.matter || !entry.description || !entry.hours) return;
    
    // Prevent concurrent saves for the same entry
    const saveKey = entry.id ? `id_${entry.id}` : `idx_${index}`;
    if (savingIds.current.has(saveKey)) return;
    savingIds.current.add(saveKey);
  
    try {
      const isNew = entry.isNew;
      const entryId = entry.id;
      const method = isNew ? 'post' : 'patch';
      const endpoint = isNew ? ROUTES.API.TIMESHEET : `${ROUTES.API.TIMESHEET}${entryId}/`;
      
      // Convert hours from HH:MM or decimal format to decimal for API
      let hours: number;
      if (entry.hours.includes(':')) {
        hours = hhmmToDecimal(entry.hours);
      } else {
        hours = parseFloat(entry.hours.toString());
      }
      
      // Convert billable hours from HH:MM or decimal format to decimal for API
      let billableHours: number;
      if (entry.billable_hours !== undefined && entry.billable_hours !== '') {
        if (typeof entry.billable_hours === 'string' && entry.billable_hours.includes(':')) {
          billableHours = hhmmToDecimal(entry.billable_hours);
        } else {
          billableHours = parseFloat(entry.billable_hours.toString());
        }
      } else {
        billableHours = entry.billable ? hours : 0;
      }
      
      // Workers write `theoretical_billable_hours` (the recorded number).
      // The backend strips `billable_hours` from non-admin writes anyway,
      // but we send theoretical alongside so admins editing the same form
      // see consistent values.
      const safeBillable = isNaN(billableHours) ? 0 : billableHours;
      const payload = {
        date: entry.date,
        matter: entry.matter,
        description: entry.description,
        hours: isNaN(hours) ? 0 : hours,
        billable: entry.billable,
        user: isNew ? (entry.user || (filters.userIds.length === 1 ? filters.userIds[0] : user?.id)) : entry.user,
        billable_hours: safeBillable,
        theoretical_billable_hours: safeBillable,
        rate: entry.rate,
        fixed_fee: entry.fixed_fee || null,
        fee: entry.fee,
        net_rate: entry.net_rate,
      };
      
      const response = await api[method](endpoint, payload);
      
      // Use functional update to merge server response without overwriting concurrent user edits
      setEntries(prev => {
        let targetIndex: number;
        if (entryId) {
          targetIndex = prev.findIndex(e => e.id === entryId);
        } else {
          // For new entries, use index position
          targetIndex = index < prev.length ? index : -1;
        }
        
        if (targetIndex === -1) return prev;
        
        const updatedEntries = [...prev];
        const currentEntry = updatedEntries[targetIndex];
        
        // Spread currentEntry first to preserve user's latest local edits,
        // then override only server-managed fields
        updatedEntries[targetIndex] = { 
          ...currentEntry,
          id: response.data.id,
          fee: response.data.fee,
          fixed_fee: response.data.fixed_fee,
          rate: response.data.rate,
          net_rate: response.data.net_rate,
          created_at: response.data.created_at,
          updated_at: response.data.updated_at,
          locked: response.data.locked,
          invoiced: response.data.invoiced,
          timer_active: response.data.timer_active,
          timer_start: response.data.timer_start,
          reviewed: response.data.reviewed,
          isNew: false,
          matterName: formatMatterName(response.data.matter)
        };
        return updatedEntries;
      });
      
      toast({ 
        title: 'Success', 
        description: isNew ? 'Time entry saved' : 'Time entry updated' 
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: 'Error', description: 'Failed to save time entry', variant: 'destructive' });
    } finally {
      savingIds.current.delete(saveKey);
    }
  };

  const deleteEntry = async (index: number) => {
    const entry = entriesRef.current[index];
    if (!entry) return;
    
    if (entry.isNew) {
      setEntries(prev => prev.filter((_, i) => i !== index));
      return;
    }

    try {
      await api.delete(`${ROUTES.API.TIMESHEET}${entry.id}/`);
      setEntries(prev => prev.filter(e => e.id !== entry.id));
      toast({ title: 'Success', description: 'Time entry deleted' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete time entry', variant: 'destructive' });
    }
  };

  const duplicateEntry = (index: number) => {
    setEntries(prev => {
      const originalEntry = prev[index];
      if (!originalEntry) return prev;
      
      const newEntry: TimesheetEntry = {
        ...originalEntry,
        id: undefined,
        date: todayStr,
        isNew: true,
        timer_active: false,
        reviewed: false,
        locked: false,
        invoiced: false,
      };
      
      const updatedEntries = [...prev];
      updatedEntries.splice(index + 1, 0, newEntry);
      return updatedEntries;
    });
  };

  const handleStartTimer = async (id: number) => {
    try {
      await api.post(`${ROUTES.API.TIMESHEET}${id}/start_timer/`);
      toast({ title: 'Success', description: 'Timer started' });
      fetchEntries();
    } catch (error) {
      showErrorToast(error, 'Error', 'Failed to start timer');
    }
  };

  const handleStopTimer = async (id: number) => {
    try {
      await api.post(`${ROUTES.API.TIMESHEET}${id}/stop_timer/`);
      toast({ title: 'Success', description: 'Timer stopped' });
      fetchEntries();
    } catch (error) {
      showErrorToast(error, 'Error', 'Failed to stop timer');
    }
  };

  const handleLockEntry = async (id: number) => {
    try {
      await api.post(`${ROUTES.API.TIMESHEET}${id}/lock/`);
      toast({ title: 'Success', description: 'Entry locked' });
      fetchEntries();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to lock entry', variant: 'destructive' });
    }
  };

  const handleUnlockEntry = async (id: number) => {
    try {
      await api.post(`${ROUTES.API.TIMESHEET}${id}/unlock/`);
      toast({ title: 'Success', description: 'Entry unlocked' });
      fetchEntries();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to unlock entry', variant: 'destructive' });
    }
  };

  const handleApproveEntry = async (id: number) => {
    try {
      await api.post(`${ROUTES.API.TIMESHEET}${id}/approve/`);
      toast({ title: 'Success', description: 'Entry approved' });
      fetchEntries();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve entry', variant: 'destructive' });
    }
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.userIds.length > 0) queryParams.append('user_id', filters.userIds.join(','));
      if (filters.matterIds.length > 0) queryParams.append('matter', filters.matterIds.join(','));
      if (filters.dateFrom) queryParams.append('start_date', filters.dateFrom);
      if (filters.dateTo) queryParams.append('end_date', filters.dateTo);
      if (filters.billable !== null) queryParams.append('billable', filters.billable.toString());
      if (filters.reviewed !== null) queryParams.append('reviewed', filters.reviewed.toString());
      if (filters.invoiced !== null) queryParams.append('invoiced', filters.invoiced.toString());
      if (filters.locked !== null) queryParams.append('locked', filters.locked.toString());
      if (searchQuery) queryParams.append('search', searchQuery);
      
      const endpoint = `${ROUTES.API.TIMESHEET}export_${format}/?${queryParams}`;
      const response = await api.get(endpoint, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `timesheet_export_${format}_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Success', description: `Exported as ${format.toUpperCase()}` });
    } catch (error) {
      toast({ title: 'Error', description: `Failed to export as ${format.toUpperCase()}`, variant: 'destructive' });
    }
  };

  const toggleSelectEntry = useCallback((id: number) => {
    isAllSelectedRef.current = false;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const validEntryCount = useMemo(() => entries.filter(e => e.id && !e.isNew).length, [entries]);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (prev.size === validEntryCount) {
        isAllSelectedRef.current = false;
        return new Set();
      }
      isAllSelectedRef.current = true;
      return new Set(entries.filter(e => e.id && !e.isNew).map(e => e.id!));
    });
  }, [entries, validEntryCount]);

  const applyBulkUpdates = async (updates: Record<string, any>) => {
    if (selectedIds.size === 0) return;
    
    setIsLoading(true);
    try {
      await api.post(`${ROUTES.API.TIMESHEET}bulk_update/`, {
        ids: Array.from(selectedIds),
        ...updates
      });
      
      setSelectedIds(new Set());
      fetchEntries();
      toast({ title: 'Success', description: `Updated ${selectedIds.size} entries` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update entries', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;

    setIsLoading(true);
    try {
      let params: URLSearchParams;
      if (isAllSelectedRef.current) {
        // "Select all" — use the same filters as the list view
        params = buildSumFilterParams();
      } else {
        // Partial selection — send individual IDs
        params = new URLSearchParams();
        selectedIds.forEach(id => params.append('ids', id.toString()));
      }

      const response = await api.post(`${ROUTES.API.TIMESHEET_APPROVE}?${params.toString()}`);
      const { approved_count, already_approved } = response.data;

      setSelectedIds(new Set());
      isAllSelectedRef.current = false;
      fetchEntries();

      const parts: string[] = [];
      if (approved_count > 0) parts.push(`${approved_count} entries approved`);
      if (already_approved > 0) parts.push(`${already_approved} already approved`);

      toast({
        title: 'Success',
        description: parts.join(', ') || 'No entries to approve',
      });
    } catch (error) {
      showErrorToast(error, 'Error', 'Failed to approve entries');
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      userIds: isUserAdmin ? [] : (user?.id ? [user.id] : []),
      matterIds: [],
      dateFrom: null,
      dateTo: null,
      billable: null,
      reviewed: null,
      invoiced: null,
      locked: null,
      sortBy: ['-date'],
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const canEditEntry = useCallback((entry: TimesheetEntry) => {
    return !entry.timer_active && !entry.locked && !entry.invoiced;
  }, []);

  // Ref-stable callbacks to prevent TimesheetRow re-renders on unrelated state changes
  const callbacksRef = useRef({
    handleEntryChange, formatHoursOnBlur, formatBillableHoursOnBlur,
    deleteEntry, duplicateEntry, handleStartTimer, handleStopTimer,
    handleLockEntry, handleUnlockEntry, handleApproveEntry
  });
  callbacksRef.current = {
    handleEntryChange, formatHoursOnBlur, formatBillableHoursOnBlur,
    deleteEntry, duplicateEntry, handleStartTimer, handleStopTimer,
    handleLockEntry, handleUnlockEntry, handleApproveEntry
  };

  const stableCallbacks = useMemo<RowCallbacks>(() => ({
    onEntryChange: (index, field, value) => callbacksRef.current.handleEntryChange(index, field, value),
    onHoursBlur: (index, value) => callbacksRef.current.formatHoursOnBlur(index, value),
    onBillableHoursBlur: (index, value) => callbacksRef.current.formatBillableHoursOnBlur(index, value),
    onDelete: (index) => callbacksRef.current.deleteEntry(index),
    onDuplicate: (index) => callbacksRef.current.duplicateEntry(index),
    onStartTimer: (id) => callbacksRef.current.handleStartTimer(id),
    onStopTimer: (id) => callbacksRef.current.handleStopTimer(id),
    onLock: (id) => callbacksRef.current.handleLockEntry(id),
    onUnlock: (id) => callbacksRef.current.handleUnlockEntry(id),
    onApprove: (id) => callbacksRef.current.handleApproveEntry(id),
  }), []);

  // Multi-column sort handler
  const handleSort = useCallback((field: string, mode: 'asc' | 'desc' | 'add-asc' | 'add-desc' | 'remove') => {
    setFilters(prev => {
      const newSortBy = [...prev.sortBy];
      const ascField = field;
      const descField = `-${field}`;
      
      // Remove existing sorts for this field
      const filteredSort = newSortBy.filter(s => s !== ascField && s !== descField);
      
      if (mode === 'remove') {
        return { ...prev, sortBy: filteredSort.length > 0 ? filteredSort : ['-date'] };
      } else if (mode === 'asc') {
        return { ...prev, sortBy: [ascField] };
      } else if (mode === 'desc') {
        return { ...prev, sortBy: [descField] };
      } else if (mode === 'add-asc') {
        return { ...prev, sortBy: [...filteredSort, ascField] };
      } else if (mode === 'add-desc') {
        return { ...prev, sortBy: [...filteredSort, descField] };
      }
      return prev;
    });
    setCurrentPage(1);
  }, []);

  // Memoized context value for FilterableColumnHeader components
  const handleDateFilterChange = useCallback((newDateFrom: string | null, newDateTo: string | null) => {
    setFilters(prev => ({ ...prev, dateFrom: newDateFrom, dateTo: newDateTo }));
  }, []);

  const handlePageReset = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const columnHeaderContextValue = useMemo(() => ({
    sortBy: filters.sortBy,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    onSort: handleSort,
    onDateFilterChange: handleDateFilterChange,
    onPageReset: handlePageReset,
  }), [filters.sortBy, filters.dateFrom, filters.dateTo, handleSort, handleDateFilterChange, handlePageReset]);

  const todayDisplayStr = useMemo(() =>
    new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
    []
  );

  return (
    <Layout>
      <PageShell>
        <PageHeader
          title="Timesheets — list view"
          description={todayDisplayStr}
          actions={
            <>
              <Button variant="outline" asChild>
                <Link to={ROUTES.TIMESHEET}>
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Week view
                </Link>
              </Button>
              <Button variant="outline" onClick={() => fetchEntries()} disabled={isLoading}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </>
          }
        />
        <div className="mb-3 text-right text-xs text-muted-foreground">
          Showing {(entries.length - 1).toLocaleString('en-GB')} of {totalEntries.toLocaleString('en-GB')} entries
        </div>
        <div className="space-y-6">

        {/* Filters & Actions */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-wrap gap-2 items-center">
            {/* Search */}
            <SearchInput
              placeholder="Search entries..."
              value={searchQuery}
              onChange={setSearchQuery}
              className="w-64"
            />

            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>

            {filters.sortBy.length > 1 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setFilters(prev => ({ ...prev, sortBy: ['-date'] }));
                  setCurrentPage(1);
                }}
                className="h-8"
              >
                <X className="h-3 w-3 mr-1" />
                Clear Sorts ({filters.sortBy.length})
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            {selectedIds.size > 0 && canManageEntries && (
              <Button
                variant="default"
                size="sm"
                onClick={handleBulkApprove}
                disabled={isLoading}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve ({isAllSelectedRef.current ? totalEntries : selectedIds.size})
              </Button>
            )}

            {selectedIds.size > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setBulkEditOpen(true)}
              >
                <FileEdit className="h-4 w-4 mr-2" />
                Edit ({selectedIds.size})
              </Button>
            )}

            {/* Write-off lives on the admin review page (/timesheets-review),
                where it belongs with the other bulk reconciliation actions. */}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  Export PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('excel')}>
                  Export Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Time Entries</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <ColumnHeaderContext.Provider value={columnHeaderContextValue}>
              <div className="overflow-x-auto">
                <Table className="table-fixed w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 px-2">
                        <Checkbox 
                          checked={selectedIds.size > 0 && selectedIds.size === validEntryCount}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      {isUserAdmin && (
                        <FilterableColumnHeader
                          field="user__last_name"
                          label="User"
                          className="w-28"
                          filterType="multi-select"
                          filterOptions={userFilterOptions}
                          currentFilterValue={filters.userIds}
                          onFilterChange={(values) => setFilters(prev => ({ ...prev, userIds: values }))}
                        />
                      )}
                      <FilterableColumnHeader
                        field="date"
                        label="Date"
                        className="w-28"
                        filterType="date"
                      />
                      <FilterableColumnHeader
                        field="matter__name"
                        label="Matter"
                        className="w-32"
                        filterType="multi-select"
                        filterOptions={matterFilterOptions}
                        currentFilterValue={filters.matterIds}
                        onFilterChange={(values) => setFilters(prev => ({ ...prev, matterIds: values }))}
                      />
                      <TableHead className="w-48 px-2 font-semibold">Description</TableHead>
                      <FilterableColumnHeader
                        field="hours"
                        label="Hours"
                        className="w-20"
                      />
                      <FilterableColumnHeader
                        field="billable"
                        label="Billable"
                        className="w-16"
                        filterType="boolean"
                        currentFilterValue={filters.billable}
                        onFilterChange={(value) => setFilters(prev => ({ ...prev, billable: value }))}
                      />
                      <TableHead className="w-16 px-2"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Selection summary row */}
                    {selectedIds.size > 0 && (
                      <TableRow className="bg-primary/5 border-b-2 border-primary/20 sticky top-0 z-10">
                        <TableCell className="w-12 align-middle py-2 px-2">
                          <div className="flex items-center justify-center">
                            <span className="text-xs font-bold text-primary">Σ</span>
                          </div>
                        </TableCell>
                        {isUserAdmin && (
                          <TableCell className="w-28 py-2">
                            <span className="text-sm font-semibold text-primary">
                              {isAllSelectedRef.current ? totalEntries : selectedIds.size} selected
                            </span>
                          </TableCell>
                        )}
                        <TableCell className="w-28 align-middle py-2">
                          {!isUserAdmin && (
                            <span className="text-sm font-semibold text-primary">
                              {isAllSelectedRef.current ? totalEntries : selectedIds.size} selected
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="w-32 align-middle py-2" />
                        <TableCell className="w-48 py-2">
                          <span className="text-sm font-medium text-muted-foreground">
                            Selection totals
                          </span>
                        </TableCell>
                        <TableCell className="w-20 align-middle py-2">
                          <div className="text-sm font-semibold text-center">
                            {isSumLoading ? (
                              <span className="text-muted-foreground">…</span>
                            ) : selectionSum ? (
                              decimalToHHMM(selectionSum.hours)
                            ) : '—'}
                          </div>
                        </TableCell>
                        <TableCell className="w-16 align-middle py-2" />
                        <TableCell className="w-16 align-middle py-2" />
                      </TableRow>
                    )}
                    {entries.map((entry, index) => (
                      <TimesheetRow
                        key={entry.id || `new-${index}`}
                        entry={entry}
                        index={index}
                        isSelected={entry.id ? selectedIds.has(entry.id) : false}
                        isUserAdmin={isUserAdmin}
                        canManageEntries={canManageEntries}
                        canEdit={canEditEntry(entry)}
                        users={users}
                        matters={matters}
                        userMap={userMap}
                        formatMatterName={formatMatterName}
                        onToggleSelect={toggleSelectEntry}
                        callbacks={stableCallbacks}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
              </ColumnHeaderContext.Provider>
            )}

            {/* Pagination */}
            <TablePagination
              className="p-4 border-t"
              currentPage={currentPage}
              totalItems={totalEntries}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              itemLabel="entries"
            />
          </CardContent>
        </Card>
      </div>

      {/* Bulk Edit Dialog */}
      <BulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        selectedCount={selectedIds.size}
        onApply={applyBulkUpdates}
        matters={matters}
        users={users}
        canManageUsers={isUserAdmin}
      />

      </PageShell>
    </Layout>
  );
}